// Display helpers shared by the Prospect/Customer Call Log grid and its Call Detail popup, so both show a call the
// same way.

import { ICommCallTranscript } from 'src/app/Shared/models/CommunicationCenter';

export interface ICallTranscriptTurn {
  name: string;
  // Caller lines sit on the right; agent lines, and lines whose speaker is unknown, on the left.
  isCaller: boolean;
  text: string;
}

// Agent = the party with a RingCentral extension; names prefer the SSM employee / customer, then RingCentral's.
// Transcripts saved before speakers were captured have no speaker list - their parties are numbered by first
// appearance and not assigned a side, since there's no way to tell who is who.
export function buildCallTranscriptTurns(transcript: ICommCallTranscript, agentName?: string, customerName?: string): ICallTranscriptTurn[] {
  const unnamedSpeakers: string[] = [];
  return transcript.Lines.map(line => {
    const speaker = transcript.Speakers.find(s => s.SpeakerId === line.SpeakerId);
    if (speaker) {
      const isCaller = !speaker.ExtensionId;
      const name = (isCaller ? customerName : agentName) || speaker.Name || (isCaller ? 'Customer' : 'Agent');
      return { name, isCaller, text: line.Text };
    }
    if (!unnamedSpeakers.includes(line.SpeakerId)) {
      unnamedSpeakers.push(line.SpeakerId);
    }
    return { name: `Speaker ${unnamedSpeakers.indexOf(line.SpeakerId) + 1}`, isCaller: false, text: line.Text };
  });
}

// "Reyes, Daniel" -> "Daniel Reyes", as the transcript names the caller; any other format is returned as is.
export function firstLastName(name?: string): string {
  const parts = (name || '').split(',');
  return parts.length === 2 ? `${parts[1].trim()} ${parts[0].trim()}`.trim() : (name || '');
}

export function callScoreClass(score?: number): string {
  if (score >= 85) {
    return 'score-high';
  }
  return score >= 70 ? 'score-mid' : 'score-low';
}

export function callSentimentClass(sentiment: string): string {
  switch ((sentiment || '').toLowerCase()) {
    case 'positive': return 'sentiment-positive';
    case 'negative': return 'sentiment-negative';
    default: return 'sentiment-neutral';
  }
}

// Plain arrow characters - Bootstrap Icons are only loaded inside the admin Communication Center module.
export function callDirectionArrow(direction: string): string {
  return (direction || '').toLowerCase() === 'outbound' ? '↗' : '↙';
}

// US numbers (10 digits, optionally with a leading 1 / +1) as "(555) 240-3391"; anything else is shown as stored.
export function formatCallerId(phoneNumber: string): string {
  const digits = (phoneNumber || '').replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (national.length !== 10) {
    return phoneNumber || '-';
  }
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

export function formatCallDuration(totalSeconds?: number): string {
  if (totalSeconds == null || isNaN(totalSeconds)) {
    return '0:00';
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
